import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Lightbulb, TrendingUp } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type Suggestion = Tables<"knowledge_base_suggestions">;

export const KnowledgeBaseSuggestions = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchSuggestions();

    const channel = supabase
      .channel('kb-suggestions-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'knowledge_base_suggestions'
        },
        () => {
          fetchSuggestions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchSuggestions = async () => {
    const { data, error } = await supabase
      .from('knowledge_base_suggestions')
      .select('*')
      .order('frequency_count', { ascending: false });

    if (error) {
      console.error('Error fetching suggestions:', error);
      toast({
        title: "Error",
        description: "Failed to load suggestions",
        variant: "destructive",
      });
    } else {
      setSuggestions(data || []);
    }
    setLoading(false);
  };

  const handleApprove = async (suggestion: Suggestion) => {
    // Add to knowledge base
    const { error: kbError } = await supabase
      .from('knowledge_base')
      .insert({
        question: suggestion.suggested_question,
        answer: suggestion.suggested_answer,
        category: suggestion.category || 'FAQ',
        is_active: true,
        priority: 5
      });

    if (kbError) {
      toast({
        title: "Error",
        description: "Failed to add to knowledge base",
        variant: "destructive",
      });
      return;
    }

    // Update suggestion status
    const { error: updateError } = await supabase
      .from('knowledge_base_suggestions')
      .update({ 
        status: 'approved',
        reviewed_by: (await supabase.auth.getUser()).data.user?.id 
      })
      .eq('id', suggestion.id);

    if (updateError) {
      toast({
        title: "Error",
        description: "Failed to update suggestion",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Added to knowledge base",
      });
    }
  };

  const handleReject = async (id: string) => {
    const { error } = await supabase
      .from('knowledge_base_suggestions')
      .update({ 
        status: 'rejected',
        reviewed_by: (await supabase.auth.getUser()).data.user?.id 
      })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to reject suggestion",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Suggestion rejected",
      });
    }
  };

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const approvedCount = suggestions.filter(s => s.status === 'approved').length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;

  if (loading) {
    return <div>Loading suggestions...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Lightbulb className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingSuggestions.length}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{approvedCount}</div>
            <p className="text-xs text-muted-foreground">
              Added to knowledge base
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rejectedCount}</div>
            <p className="text-xs text-muted-foreground">
              Not suitable
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>AI-Suggested Knowledge Base Entries</CardTitle>
          <CardDescription>
            Review and approve suggestions learned from agent conversations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingSuggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No pending suggestions. AI will learn from agent conversations!
            </p>
          ) : (
            <div className="space-y-4">
              {pendingSuggestions.map((suggestion) => (
                <div key={suggestion.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {suggestion.category && (
                          <Badge variant="outline">{suggestion.category}</Badge>
                        )}
                        {suggestion.frequency_count > 1 && (
                          <Badge variant="secondary" className="gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {suggestion.frequency_count}x
                          </Badge>
                        )}
                      </div>
                      <h4 className="font-medium mb-1">Q: {suggestion.suggested_question}</h4>
                      <p className="text-sm text-muted-foreground">A: {suggestion.suggested_answer}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleApprove(suggestion)}
                      className="gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve & Add
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleReject(suggestion.id)}
                      className="gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
